import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiPaginatedResponse, IApiResponse } from "@/types";
import type { IEventStats } from "@/types/(company)/events.interface";
import type {
  ICalendarEventResponse,
  IEventInvitationDetails,
  ISingleEventInvitation,
  IUpcomingEvent,
} from "@/types/(my-space)/events.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const mySpaceEventsApi = createApi({
  reducerPath: "mySpaceEventsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["GetAllEventInvitations", "GetEventStats", "GetEventDetailsById", "GetUpcomingEvents"],
  endpoints: (builder) => ({
    msGetAllEvents: builder.query<IApiPaginatedResponse<ISingleEventInvitation[]>, IApiGeneralParams>({
      query: (params) => ({
        url: APIEndpoints.MySpace.Events.GetAllEvents,
        params,
      }),
      providesTags: ["GetAllEventInvitations"],
    }),
    msGetEventStats: builder.query<IApiPaginatedResponse<IEventStats>, void>({
      query: () => ({
        url: APIEndpoints.MySpace.Events.GetEventStats,
      }),
      providesTags: ["GetEventStats"],
    }),
    msGetEventById: builder.query<IApiPaginatedResponse<IEventInvitationDetails>, { eventId: string }>({
      query: ({ eventId }) => ({
        url: APIEndpoints.MySpace.Events.GetEventById(eventId),
      }),
      providesTags: ["GetEventDetailsById"],
    }),
    msAcceptEventInvitation: builder.mutation<IApiPaginatedResponse<void>, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: APIEndpoints.MySpace.Events.AcceptEventInvitation(invitationId),
        method: "PUT",
      }),
      invalidatesTags: ["GetAllEventInvitations", "GetEventDetailsById"],
    }),
    msRejectEventInvitation: builder.mutation<IApiPaginatedResponse<void>, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: APIEndpoints.MySpace.Events.RejectEventInvitation(invitationId),
        method: "PUT",
      }),
      invalidatesTags: ["GetAllEventInvitations"],
    }),
    msGetEventsCalendar: builder.query<IApiPaginatedResponse<ICalendarEventResponse>, { year: string; month: string }>({
      query: (params) => ({
        url: APIEndpoints.MySpace.Events.GetEventsCalendar,
        params,
      }),
    }),
    getUpcomingEvents: builder.query<IApiResponse<IUpcomingEvent[]>, { limit?: number }>({
      query: (params) => ({
        url: APIEndpoints.MySpace.Events.GetUpcomingEvents,
        params,
      }),
      providesTags: ["GetUpcomingEvents"],
    }),
  }),
});

export const {
  useMsGetAllEventsQuery,
  useMsGetEventByIdQuery,
  useMsGetEventStatsQuery,
  useMsAcceptEventInvitationMutation,
  useMsRejectEventInvitationMutation,
  useMsGetEventsCalendarQuery,
  useGetUpcomingEventsQuery,
} = mySpaceEventsApi;
